import { useMemo } from "react";
import type { components } from "@/lib/api/types";
import type { Tool } from "@/lib/utils/task-form";

type UserIntegration = components["schemas"]["IntegrationResponse"];
type AppIntegration = components["schemas"]["AppIntegrationResponse"];

/**
 * Hook to build combined apps list from user integrations and app catalog.
 *
 * Shows ALL apps from the catalog, sorted by:
 * 1. Connected apps first (user has set up)
 * 2. Popular apps second (is_popular flag)
 * 3. Alphabetically
 *
 * @param userIntegrations - User's connected integrations
 * @param appIntegrations - Available integrations from app catalog
 * @returns Combined array of all apps with connection status
 */
export function useCombinedTools(
	userIntegrations: UserIntegration[],
	appIntegrations: AppIntegration[]
): Tool[] {
	return useMemo(() => {
		// Build map of user's connected integrations by app_integration_id
		const connectedByAppId = new Map<number, UserIntegration>();
		for (const ui of userIntegrations) {
			const appIntegrationId = Number(ui.app_integration_id);
			if (Number.isFinite(appIntegrationId)) {
				connectedByAppId.set(appIntegrationId, ui);
			}
		}

		// Convert all app integrations to Tool format
		const tools: Tool[] = appIntegrations.map((app) => {
			const userIntegration = connectedByAppId.get(app.id);
			const isActive = !!userIntegration;

			return {
				id: String(app.id),
				name: app.name,
				appIntegrationId: app.id,
				isActive,
				integrationId: userIntegration ? Number(userIntegration.id) : undefined,
				provider: app.provider,
				logoUrl: app.logo_url,
				isPopular: app.is_popular,
			};
		});

		// Sort: connected first, then popular, then alphabetically
		return tools.sort((a, b) => {
			// Connected first
			if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;

			// Popular second
			if (a.isPopular !== b.isPopular) return a.isPopular ? -1 : 1;

			// Alphabetical
			return a.name.localeCompare(b.name);
		});
	}, [appIntegrations, userIntegrations]);
}
